import { BellOff } from "lucide-react";
import { useLeadOptOut, useSetLeadOptOut } from "@/hooks/useLeadOptOut";

const SOURCE_LABEL: Record<string, string> = {
  keyword_reply: "Lo pidió por WhatsApp",
  meta_status: "Meta reportó que no acepta mensajes de marketing",
  manual: "Marcado en el CRM",
};

/**
 * Whether this prospect asked to stop receiving bulk sends, and the switch to
 * change it by hand.
 *
 * The manual path exists because the keyword match is deliberately exact — "ya
 * no me manden nada" written in prose never trips it — and a human reading the
 * chat is the only reliable classifier we have for that long tail.
 *
 * Undoing one asks for confirmation: it means writing again to someone who asked
 * us not to.
 */
export function LeadOptOutBadge({
  leadId,
  hasPhone,
}: {
  leadId: string;
  hasPhone: boolean;
}): React.JSX.Element | null {
  const { data } = useLeadOptOut(hasPhone ? leadId : undefined);
  const setOptOut = useSetLeadOptOut(leadId);

  if (!hasPhone) return null;

  if (!data?.optedOut) {
    return (
      <button
        type="button"
        disabled={setOptOut.isPending}
        onClick={() => {
          setOptOut.mutate(true);
        }}
        className="text-xs text-ink-500 underline hover:text-ink-700 disabled:opacity-50"
      >
        Marcar «no recibe envíos masivos»
      </button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
        title={`${SOURCE_LABEL[data.source ?? ""] ?? "Baja registrada"}${
          data.byName ? ` · ${data.byName}` : ""
        }${data.createdAt ? ` · ${new Date(data.createdAt).toLocaleDateString("es-MX")}` : ""}`}
      >
        <BellOff size={12} />
        No recibe envíos masivos
      </span>
      <button
        type="button"
        disabled={setOptOut.isPending}
        onClick={() => {
          if (
            window.confirm(
              "Esta persona pidió no recibir envíos masivos. ¿Quitar la baja y volver a incluirla?",
            )
          ) {
            setOptOut.mutate(false);
          }
        }}
        className="text-xs text-ink-500 underline hover:text-ink-700 disabled:opacity-50"
      >
        Quitar
      </button>
    </span>
  );
}
